import React, { Component, useRef, useState } from "reactn";
import sanitize from "sanitize-filename";
import styled from "styled-components";
import { MenuItem, MenuList, Popover } from "@material-ui/core";
import { MoreVert as MoreVertIcon } from "@material-ui/icons";

import { AiImageStyleTypes, AiImageStyles, AiImageModel, AiImagePresetStyles } from "common/aiConstants";
import { ASSET_RESOLUTION, AssetType } from "common/constants";
import { getStaticUrl } from "js/config";
import WorkspaceController from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { ds } from "js/core/models/dataService";
import AiGenerationService, { InsufficientCreditsError, TooManyRequestsError } from "js/core/services/aiGeneration";
import { loadImage } from "js/core/utilities/promiseHelper";
import { downloadFromUrl, trackActivity } from "js/core/utilities/utilities";
import { ShowDialogAsync, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import { ShowInsufficientCreditsDialog, ShowTooManyRequestsDialog } from "js/react/components/Dialogs/CreditsErrorDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { Notice } from "js/react/components/Notice";
import { BlueButton, UIContainerContext, UIPane, UIPaneContents, UIPaneHeader } from "js/react/components/UiComponents";
import ImageThumbnailGrid from "js/react/views/AddAssets/Components/ImageThumbnailGrid";
import EndTrialDialog from "js/react/views/Billing/dialogs/EndTrialDialog";
import { AiImageStylePopup } from "js/editor/AiGeneration/GenerateImage/AiImageStyle";
import PresentationEditorController from "js/editor/PresentationEditor/PresentationEditorController";
import { FlexLayout } from "js/Components/FlexLayout";
import PromptInput from "js/react/views/AIGeneration/components/PromptInput";
import { Gap10 } from "js/react/components/Gap";
import { TextContent } from "js/Components/TextContent";

const logger = getLogger(LogGroup.AI);

const ThumbnailContainer = styled.div.attrs(({ width, height }) => ({
    style: {
        width: `${width}px`,
        ...(height ? { height: `${height}px` } : {})
    }
}))`
    position: relative;
`;

const ThumbnailContainerImage = styled.img.attrs(({ showOutline }) => ({
    style: {
        outline: showOutline ? "1px solid #50bbe6" : "none"
    }
}))`
    width: 100%;
    display: block;
`;

const ThumbnailContainerBottomBar = styled.div.attrs(({ visible }) => ({
    style: {
        opacity: visible ? 1 : 0
    }
}))`
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    background: black;
    padding: 5px 10px;
    transition: opacity 300ms;
    display: flex;
    align-items: center;
    justify-content: space-between;

    >span {
        font-weight: 600;
        font-size: 12px;
        line-height: 150%;
        color: #FFFFFF;
        margin-right: 10px;
    }

    >div {
        border-radius: 50%;
        min-width: 30px;
        width: 30px;
        height: 30px;
        background: white;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;

        &:hover {
            color: #fefefe;
        }

        >svg {
            color: #666666;
        }
    }
`;

const LoadingMoreContainer = styled.div`
    padding: 20px;
    text-align: center;
    color: #666;
`;

function Thumbnail({ width, height, readUrl, attribution, assetId, onSelect, onSetSearchQuery, onDelete }) {
    const ref = useRef();

    const [isPopoverOpen, setPopoverOpen] = useState(false);
    const [isHoveringOverContainer, setIsHoveringOverContainer] = useState(false);

    const handleOpenPopover = () => {
        setPopoverOpen(true);
    };

    const handleClosePopover = () => {
        setPopoverOpen(false);
        setIsHoveringOverContainer(false);
    };

    const handleMouseEnter = () => {
        setIsHoveringOverContainer(true);
    };

    const handleMouseLeave = () => {
        if (!isPopoverOpen) {
            setIsHoveringOverContainer(false);
        }
    };

    const handleDownload = async () => {
        try {
            let downloadUrl = readUrl;

            if (assetId) {
                const asset = await ds.assets.getAssetById(assetId, AssetType.IMAGE, 10);
                downloadUrl = await asset.getURL("original", true);
            }

            const fileName = `${sanitize(attribution)}.png`;
            await downloadFromUrl(downloadUrl, fileName, "image/png");
        } catch (err) {
            logger.error(err, "[AISearchPane] handleDownload() failed", { assetId });
            ShowErrorDialog({
                title: "Unable to download image",
                message: "Please try again in a moment."
            });
        } finally {
            handleClosePopover();
        }
    };

    const handleSetSearchQuery = () => {
        onSetSearchQuery(attribution);
        handleClosePopover();
    };

    const handleDelete = () => {
        if (!assetId) {
            return;
        }
        onDelete?.(assetId);
        handleClosePopover();
    };

    return (<>
        <ThumbnailContainer
            width={width}
            height={height}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <ThumbnailContainerImage
                src={readUrl}
                onClick={onSelect}
                showOutline={isHoveringOverContainer}
            />
            <ThumbnailContainerBottomBar
                visible={isHoveringOverContainer}
            >
                <span>{attribution}</span>
                <div
                    ref={ref}
                    onClick={handleOpenPopover}
                >
                    <MoreVertIcon />
                </div>
            </ThumbnailContainerBottomBar>
        </ThumbnailContainer>
        <Popover
            open={isPopoverOpen}
            anchorEl={ref.current}
            anchorOrigin={{
                vertical: "top",
                horizontal: "center",
            }}
            transformOrigin={{
                vertical: "bottom",
                horizontal: "center",
            }}
            onClose={handleClosePopover}
        >
            <MenuList>
                <MenuItem value={0} onClick={handleDownload}>Download Image</MenuItem>
                <MenuItem value={1} onClick={handleSetSearchQuery}>Use this prompt again</MenuItem>
                <MenuItem value={2} onClick={handleDelete} disabled={!assetId}>Delete Image</MenuItem>
            </MenuList>
        </Popover>
    </>);
}

class AISearchPane extends Component {
    static contextType = UIContainerContext;
    static searchResultsLength = 1;

    constructor(props) {
        super(props);

        const presentationMetadata = PresentationEditorController.presentation?.get("metadata");
        const theme = PresentationEditorController.currentTheme;

        this.state = {
            isLoading: true,
            isSearching: false,
            searchQuery: "",
            searchResults: [],
            creditsBalance: 0,
            workspaceId: AppController.workspaceId,
            theme,
            isStyleSelectMenuOpen: false,
            imageStyle: (!!presentationMetadata?.imageStyle && presentationMetadata.imageStyle !== "stock") ? presentationMetadata.imageStyle : "theme",
            imageStylePrompt: this.getImageStylePrompt(theme),
            allSortedAssets: [],
            displayedCount: 0,
            isLoadingMore: false,
            hasMoreAssets: false
        };

        this.promptInputRef = React.createRef();
        this.thumbnailButtonsRefs = [...new Array(AISearchPane.searchResultsLength)].map(() => React.createRef());
        this.loadMoreTriggerRef = React.createRef();
        this.intersectionObserver = null;
    }

    getImageStylePrompt(theme) {
        const presentationMetadata = PresentationEditorController.presentation?.get("metadata");
        return presentationMetadata?.imageStylePrompt ? presentationMetadata.imageStylePrompt : (!!theme && theme.has("imageStylePrompt") ? theme.get("imageStylePrompt") : AiImageStyles[AiImageStyleTypes.MINIMALIST].prompt);
    }

    componentDidMount() {
        (async () => {
            if (!this.state.theme) {
                const themes = await WorkspaceController.getSharedThemes(true, true);
                if (themes.length > 0) {
                    this.setState({
                        theme: themes[0],
                        imageStylePrompt: this.getImageStylePrompt(themes[0]),
                    });
                }
            }

            await Promise.all([
                this.loadCreditsBalance(),
                this.loadRecentlyUsedAssets()
            ]);

            // Set up IntersectionObserver for infinite scroll
            this.setupScrollObserver();

            // After data loads, wait 2 seconds, then unfocus the input and reveal the page.
            setTimeout(() => {
                if (document.activeElement && typeof document.activeElement.blur === "function") {
                    document.activeElement.blur();
                }
                this.setState({ isLoading: false });
            }, 2000);
        })().catch(err => {
            logger.error(err, "[AISearchPane] load failed");
            this.setState({ isLoading: false });
        });
    }

    componentWillUnmount() {
        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
        }
    }

    setupScrollObserver() {
        if (!this.loadMoreTriggerRef.current) {
            return;
        }

        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
        }

        this.intersectionObserver = new IntersectionObserver(
            entries => {
                const [entry] = entries;
                if (entry.isIntersecting && this.state.hasMoreAssets && !this.state.isLoadingMore) {
                    this.loadMoreAssets();
                }
            },
            { threshold: 0.1 }
        );

        this.intersectionObserver.observe(this.loadMoreTriggerRef.current);
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            prevState.hasMoreAssets !== this.state.hasMoreAssets ||
            prevState.searchResults.length !== this.state.searchResults.length
        ) {
            this.setupScrollObserver();
        }
    }

    async loadCreditsBalance() {
        const { workspaceId } = this.state;
        const creditsBalance = await AiGenerationService.getCreditsBalance(workspaceId);
        this.setState({ creditsBalance });
    }

    async loadRecentlyUsedAssets() {
        const { workspaceId } = this.state;

        const allAssets = await ds.assets.getAssetsByType(AssetType.IMAGE, workspaceId);

        const aiImageSources = new Set(Object.values(AiImageModel));
        const isAiGeneratedAsset = asset => {
            const source = asset.get("source");
            return asset.get("isGenerated") === true ||
                !!asset.get("aiImageStyle") ||
                source === "dall-e" ||
                aiImageSources.has(source);
        };

        const loadedAssets = [];
        const chunkSize = 20;

        for (let i = 0; i < allAssets.length; i += chunkSize) {
            const chunk = allAssets.slice(i, i + chunkSize);

            await Promise.all(chunk.map(asset => asset.load()));

            for (const asset of chunk) {
                if (isAiGeneratedAsset(asset)) {
                    loadedAssets.push(asset);
                }
            }

            if (loadedAssets.length >= 500) {
                break;
            }
        }

        const finalAssets = loadedAssets
            .sort((a, b) => {
                const userAssetA = ds.userAssets.get(a.id);
                const userAssetB = ds.userAssets.get(b.id);
                const timeA = userAssetA?.get("lastUsed") ?? userAssetA?.get("modifiedAt");
                const timeB = userAssetB?.get("lastUsed") ?? userAssetB?.get("modifiedAt");
                return timeB - timeA;
            })
            .slice(0, 500);

        this.setState({ allSortedAssets: finalAssets });

        const initialAssets = finalAssets.slice(0, 20);
        const searchResults = await Promise.all(initialAssets.map(async asset => {
            const readUrl = await asset.getURL(ASSET_RESOLUTION.MEDIUM);
            let assetId = asset.id;
            let attribution = asset.get("attribution");

            const imageStylePrompt = Object.values(AiImageStyles).find(({ prompt }) => attribution.includes(prompt))?.prompt || attribution.match(new RegExp(", in a \"" + "(.*)" + "\" style"))?.[1];
            if (imageStylePrompt) {
                attribution = attribution.substring(0, attribution.indexOf(", in a"));
            }

            return { readUrl, assetId, attribution };
        }));

        this.setState({
            searchResults,
            displayedCount: searchResults.length,
            hasMoreAssets: finalAssets.length > searchResults.length
        });
    }

    async loadMoreAssets() {
        const { allSortedAssets, displayedCount, isLoadingMore } = this.state;

        if (isLoadingMore || displayedCount >= allSortedAssets.length) {
            return;
        }

        this.setState({ isLoadingMore: true });

        try {
            const nextAssets = allSortedAssets.slice(displayedCount, displayedCount + 20);

            const newSearchResults = await Promise.all(nextAssets.map(async asset => {
                const readUrl = await asset.getURL(ASSET_RESOLUTION.MEDIUM);
                let assetId = asset.id;
                let attribution = asset.get("attribution");

                const imageStylePrompt = Object.values(AiImageStyles).find(({ prompt }) => attribution.includes(prompt))?.prompt || attribution.match(new RegExp(", in a \"" + "(.*)" + "\" style"))?.[1];
                if (imageStylePrompt) {
                    attribution = attribution.substring(0, attribution.indexOf(", in a"));
                }

                return { readUrl, assetId, attribution };
            }));

            this.setState(({ searchResults, displayedCount, allSortedAssets }) => ({
                searchResults: [...searchResults, ...newSearchResults],
                displayedCount: displayedCount + newSearchResults.length,
                hasMoreAssets: displayedCount + newSearchResults.length < allSortedAssets.length,
                isLoadingMore: false
            }));
        } catch (err) {
            logger.error(err, "[AISearchPane] loadMoreAssets() failed");
            this.setState({ isLoadingMore: false });
        }
    }

    doImageSearch = async searchQuery => {
        const { workspaceId, imageStyle, imageStylePrompt } = this.state;

        this.setState({ isSearching: true, searchQuery });

        try {
            let { response: { assets }, remainingCreditsBalance: creditsBalance } = await AiGenerationService.generateImages({
                prompt: searchQuery,
                imageStyle,
                imageStylePrompt,
                imagesCount: AISearchPane.searchResultsLength,
                model: WorkspaceController.isEnterprise ? AiImageModel.DALL_E_3 : undefined
            });

            // Preload all images for proper caching and smoother ux
            await Promise.all(assets.map(({ readUrl }) => loadImage(readUrl)));

            // Remove the style prompt from the attribution
            assets = assets.map(asset => {
                let attribution = asset.attribution;
                const imageStylePrompt = Object.values(AiImageStyles).find(({ prompt }) => attribution.includes(prompt))?.prompt || attribution.match(new RegExp(", in a \"" + "(.*)" + "\" style"))?.[1];
                if (imageStylePrompt) {
                    attribution = attribution.substring(0, attribution.indexOf(", in a"));
                }
                return { ...asset, attribution };
            });

            this.setState(({ searchResults }) => ({
                isSearching: false,
                searchResults: [...assets, ...searchResults],
                creditsBalance
            }));
            trackActivity("Image", "Generate", null, null, {
                asset_id: assets[0].assetId,
                prompt: searchQuery,
                custom_style_type: (!imageStyle || imageStyle === "stock") ? null
                    : imageStyle === "custom" ? "prompt" : "preset",
                custom_style_type_prompt: (!imageStyle || imageStyle === "stock") ? null
                    : imageStylePrompt,
                model_name: AiImageModel.GEMINI_2_5_FLASH_PREVIEW
            });
        } catch (err) {
            logger.error(err, "[AISearchPane] doImageSearch() failed", { workspaceId, searchQuery });

            if (err instanceof InsufficientCreditsError) {
                ShowInsufficientCreditsDialog(() => this.loadCreditsBalance());
            } else if (err instanceof TooManyRequestsError) {
                ShowTooManyRequestsDialog();
            } else {
                ShowErrorDialog({
                    error: "Sorry, we couldn't process your request",
                    message: err.message
                });
            }

            this.setState({
                isSearching: false,
                searchQuery: "",
                imageStylePrompt: "",
            });
        }
    }

    handleSelectImage = async assetId => {
        const { addAssetCallback, handleConfirm } = this.props;

        const asset = await ds.assets.getAssetById(assetId, AssetType.IMAGE);
        addAssetCallback(asset);

        handleConfirm();
    }

    handleSelectImageStyle = async selectedImageStyle => {
        const { theme } = this.state;

        if (selectedImageStyle === "none") {
            this.setState({ imageStyle: "none", imageStylePrompt: "" });
            if (PresentationEditorController.presentation) {
                PresentationEditorController.presentation.update({ metadata: { ...PresentationEditorController.presentation.get("metadata"), imageStyle: "none" } });
                await PresentationEditorController.presentation.updatePromise;
            }
            return;
        }

        if (selectedImageStyle === "**theme**") {
            const imageStylePrompt = theme.get("imageStylePrompt") ?? AiImageStyles[AiImageStyleTypes.MINIMALIST].prompt;
            this.setState({ imageStyle: "**theme**", imageStylePrompt });
            if (PresentationEditorController.presentation) {
                PresentationEditorController.presentation.update({ metadata: { ...PresentationEditorController.presentation.get("metadata"), imageStyle: "**theme**" } });
                await PresentationEditorController.presentation.updatePromise;
            }
            return;
        }

        const presetStyle = AiImagePresetStyles.find(style => style.style === selectedImageStyle);
        if (presetStyle) {
            this.setState({
                imageStyle: presetStyle.style,
                imageStylePrompt: presetStyle.prompt
            });
            if (PresentationEditorController.presentation) {
                PresentationEditorController.presentation.update({ metadata: { ...PresentationEditorController.presentation.get("metadata"), imageStyle: presetStyle.style } });
                await PresentationEditorController.presentation.updatePromise;
            }
            return;
        }

        this.setState({ imageStyle: "custom", imageStylePrompt: selectedImageStyle });
        if (PresentationEditorController.presentation) {
            PresentationEditorController.presentation.update({
                metadata: {
                    ...PresentationEditorController.presentation.get("metadata"),
                    imageStyle: "custom",
                    imageStylePrompt: selectedImageStyle
                }
            });
            await PresentationEditorController.presentation.updatePromise;
        }
    }

    handleStyleSelected = selectedStyle => {
        this.setState(({ searchQuery }) => ({ searchQuery: `${searchQuery}, ${selectedStyle}` }));
    }

    handleSetSearchQuery = searchQuery => {
        this.setState({ searchQuery });
    }

    handleEnterKeyPress = searchQuery => {
        this.doImageSearch(searchQuery);
    }

    handleEndTrial = () => {
        ShowDialogAsync(EndTrialDialog)
            .then(() => this.loadCreditsBalance());
    };

    handleRemoveImage = assetId => {
        const userAsset = ds.userAssets.get(assetId);
        if (userAsset) {
            ds.userAssets.remove(userAsset);
        }

        this.setState(({ searchResults }) => ({
            searchResults: searchResults.filter(result => result.assetId !== assetId)
        }));
    }

    render() {
        const {
            isLoading,
            isSearching,
            searchResults,
            searchQuery,
            imageStyle,
            theme,
        } = this.state;

        const uiPaneContentsStyle = { paddingTop: 20, position: "relative" };

        return (
            <UIPane>
                <FetchingClickShield visible={isLoading} backgroundColor="white" />

                <FlexLayout padding={20} background="#eee" minHeight={220}>
                    <FlexLayout gap={10} padding={"0 20px"} background="white" width="fill">
                        <FlexLayout gap={10} direction="column" grow={1}>
                            <Gap10 />
                            <FlexLayout height={28}>
                                <TextContent header>Generate an image with AI</TextContent>
                            </FlexLayout>
                            <PromptInput
                                ref={this.promptInputRef}
                                prompt={searchQuery}
                                onPromptChange={this.handleSetSearchQuery}
                                onEnterKeyPress={this.handleEnterKeyPress}
                                promptPlaceholder="Describe your image in detail..."
                                hideExamplePrompts
                                inputContainerHeight={72}
                                inputDisabled={isSearching}
                                multiline={true}
                            />
                        </FlexLayout>
                        <FlexLayout direction="column" grow={0}>
                            <FlexLayout gap={10} justify="space-between" direction="column">
                                <Gap10 />
                                <FlexLayout height={27}>
                                    <TextContent center popupLabel>Image Style</TextContent>
                                </FlexLayout>
                                <AiImageStylePopup
                                    theme={theme}
                                    imageStyle={imageStyle === "theme" ? "**theme**" : (imageStyle === "custom" ? this.state.imageStylePrompt : imageStyle)}
                                    onChange={this.handleSelectImageStyle}
                                />
                            </FlexLayout>
                        </FlexLayout>
                    </FlexLayout>
                </FlexLayout>

                <UIPaneContents style={uiPaneContentsStyle}>
                    {isSearching &&
                        <Notice
                            image={getStaticUrl("/images/bai-bot/bai-bot-happy.png")}
                            imageWidth={100}
                            showProgressBar
                            title="Generating your image... Please wait."
                        />
                    }
                    {!isSearching && <>
                        {searchResults.length > 0 &&
                            <>
                                <ImageThumbnailGrid columns={4}>
                                    {searchResults.map(({ assetId, attribution, imageStylePrompt, readUrl }) => (
                                        <Thumbnail
                                            key={assetId}
                                            readUrl={readUrl}
                                            assetId={assetId}
                                            attribution={attribution}
                                            imageStyle={imageStyle}
                                            imageStylePrompt={imageStylePrompt}
                                            onSelect={() => this.handleSelectImage(assetId)}
                                            onSetSearchQuery={searchQuery => {
                                                this.promptInputRef.current.typePrompt(searchQuery).then(() => {
                                                    this.handleSetSearchQuery(searchQuery);
                                                });
                                            }}
                                            onDelete={this.handleRemoveImage}
                                        />
                                    ))}
                                </ImageThumbnailGrid>
                                <div ref={this.loadMoreTriggerRef} />
                                {this.state.hasMoreAssets && this.state.isLoadingMore && (
                                    <LoadingMoreContainer>
                                        Loading additional assets...
                                    </LoadingMoreContainer>
                                )}
                            </>
                        }
                        {searchResults.length === 0 &&
                            <Notice
                                image={getStaticUrl("/images/ai-search/ai-search.gif")}
                                imageWidth={400}
                                title="Describe what you are looking for and let DesignerBot generate an image unique to your story."
                            />
                        }
                    </>}
                </UIPaneContents>

                <FlexLayout width="fill" padding={20} align="center" justify="flex-end">
                    <BlueButton
                        disabled={isSearching || searchQuery.trim().length === 0}
                        onClick={() => this.doImageSearch(searchQuery)}
                    >Generate Image</BlueButton>
                </FlexLayout>
            </UIPane>
        );
    }
}

export default AISearchPane;
